import React, { useEffect, useRef, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { HCard, FieldLabel } from '../h';
import {
  ALLOWED_SHIPPING_COUNTRIES,
  getPostcodeLabel,
  getStateConfig,
  validateState,
  displayState,
} from '../../utils/shippingCountries';
import { cn } from '../../lib/utils';

/* Reusable address form, used by both the "Same address" mode and the
   "Edit one Hearth" sub-view of ShippingAddressConfirmation.

   Loads Google Maps Places lazily and attaches an Autocomplete to the
   Address Line 1 input. While the script is loading the input still works —
   the user can type freely and the autocomplete just won't suggest. A
   "Enter manually" link tears the autocomplete off if the user prefers.

   Renders a single <HCard> containing field rows separated by hairline
   dividers. Field IDs are prefixed with `idPrefix` so multiple instances
   can coexist on the same page. */
export function ShippingAddressForm({ idPrefix, value, onChange, autoFocus }) {
  const safe = value || {};
  const country = safe.country || 'GB';

  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(
    typeof window !== 'undefined' && !!window.google?.maps?.places,
  );

  /* Google Maps loader — shared globally so we only inject the script once
     across all instances of this form. */
  useEffect(() => {
    if (googleReady) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (window.google?.maps?.places) {
      setGoogleReady(true);
      return undefined;
    }
    const existing = document.querySelector('script[data-higlo-maps]');
    if (existing) {
      const poll = setInterval(() => {
        if (window.google?.maps?.places) {
          setGoogleReady(true);
          clearInterval(poll);
        }
      }, 100);
      return () => clearInterval(poll);
    }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return undefined; // No key in dev → silently skip; manual entry still works.
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.dataset.higloMaps = '1';
    script.onload = () => {
      const poll = setInterval(() => {
        if (window.google?.maps?.places) {
          setGoogleReady(true);
          clearInterval(poll);
        }
      }, 100);
      // Bail out after 10s rather than polling forever.
      setTimeout(() => clearInterval(poll), 10_000);
    };
    document.head.appendChild(script);
    return undefined;
  }, [googleReady]);

  /* Refs mirroring the latest props, so the place_changed listener (which
     we attach once on Maps-ready and never reattach) reads fresh values
     instead of a frozen closure. Without this, picking an autocomplete
     suggestion would wipe any field the user had since typed (e.g. the
     recipient name) and reset the country to its initial value, because
     the merge target `value` was captured at first mount. */
  const valueRef = useRef(value);
  const countryRef = useRef(country);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  useEffect(() => {
    countryRef.current = country;
  }, [country]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /* Attach autocomplete once Maps is ready. The listener reads everything
     it needs from refs so it never goes stale. */
  useEffect(() => {
    if (!googleReady || !inputRef.current) return;
    if (acRef.current) return;
    try {
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: countryRef.current.toLowerCase() },
        fields: ['address_components'],
        types: ['address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        const components = place.address_components;
        const get = (types, format = 'long_name') => {
          const arr = Array.isArray(types) ? types : [types];
          for (const t of arr) {
            const c = components.find((comp) => comp.types.includes(t));
            if (c) return c[format];
          }
          return '';
        };
        const streetNumber = get('street_number');
        const route = get('route');
        const premise = get('premise');
        let line1 = '';
        if (streetNumber && route) line1 = `${streetNumber} ${route}`.trim();
        else if (route) line1 = route;
        else if (premise) line1 = premise;
        const currentCountry = countryRef.current;
        const city =
          currentCountry === 'GB'
            ? get(['postal_town', 'locality', 'administrative_area_level_2'])
            : get([
                'locality',
                'postal_town',
                'administrative_area_level_2',
                'administrative_area_level_3',
              ]);
        // State mirrors the marketing order form (site/src/lib/placeAddress.js):
        // US/CA take level 1's short code (what carriers expect) — left blank
        // if it isn't in our list, so the dropdown asks for it; the UK's level
        // 1 is the nation ("England"), so its county comes from level 2;
        // everywhere else level 1's long name.
        let state;
        if (currentCountry === 'US' || currentCountry === 'CA') {
          const code = get('administrative_area_level_1', 'short_name').toUpperCase();
          state = validateState(code, currentCountry) ? code : '';
        } else if (currentCountry === 'GB') {
          state = get('administrative_area_level_2');
        } else {
          state = get('administrative_area_level_1');
        }
        const postalCode = get('postal_code');
        onChangeRef.current({
          ...(valueRef.current || {}),
          line1,
          city,
          state,
          postalCode,
        });
      });
      acRef.current = ac;
    } catch (err) {
      console.error('Autocomplete init error', err);
    }
  }, [googleReady]);

  /* Update country restriction live when the country changes. */
  useEffect(() => {
    if (acRef.current && country) {
      acRef.current.setComponentRestrictions({ country: country.toLowerCase() });
    }
  }, [country]);

  const stateCfg = getStateConfig(country);
  // The dropdown only shows known codes; anything else (e.g. a full name
  // saved by an older version of this form) renders as unselected.
  const stateCode = displayState(safe.state, country);
  const stateSelectValue = stateCfg.options && validateState(stateCode, country) ? stateCode : '';

  const set = (field) => (e) =>
    onChange({ ...safe, [field]: typeof e === 'string' ? e : e.target.value });

  const inputClass =
    'w-full text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400 focus-visible:outline-none';
  const Divider = () => <div className="h-px bg-slate-100" />;

  return (
    <HCard className="p-4 space-y-2.5">
      <Row>
        <FieldLabel htmlFor={`${idPrefix}-name`} className="mb-0.5">
          Recipient name
        </FieldLabel>
        <input
          id={`${idPrefix}-name`}
          type="text"
          autoComplete="name"
          autoFocus={autoFocus}
          className={inputClass}
          placeholder="Full name"
          value={safe.name || ''}
          onChange={set('name')}
          required
        />
      </Row>
      <Divider />
      <Row>
        <FieldLabel htmlFor={`${idPrefix}-country`} className="mb-0.5">
          Country
        </FieldLabel>
        <Select
          value={country}
          // A state/county belongs to its country — never carry it across.
          onValueChange={(val) => onChange({ ...safe, country: val, state: '' })}
        >
          <SelectTrigger
            id={`${idPrefix}-country`}
            className="border-0 px-0 h-auto py-0 text-[14px] text-slate-900 shadow-none focus:ring-0 focus-visible:ring-0 hover:bg-transparent bg-transparent [&>span]:line-clamp-1"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALLOWED_SHIPPING_COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>
      <Divider />
      <Row>
        <FieldLabel htmlFor={`${idPrefix}-line1`} className="mb-0.5">
          Address line 1
        </FieldLabel>
        <input
          id={`${idPrefix}-line1`}
          ref={inputRef}
          type="text"
          autoComplete="address-line1"
          className={inputClass}
          placeholder="Start typing your address…"
          value={safe.line1 || ''}
          onChange={set('line1')}
          required
        />
      </Row>
      <Divider />
      <Row>
        <FieldLabel htmlFor={`${idPrefix}-line2`} className="mb-0.5">
          Address line 2{' '}
          <span className="font-normal text-slate-400">(optional)</span>
        </FieldLabel>
        <input
          id={`${idPrefix}-line2`}
          type="text"
          autoComplete="address-line2"
          className={inputClass}
          placeholder="Flat, building, etc."
          value={safe.line2 || ''}
          onChange={set('line2')}
        />
      </Row>
      <Divider />
      <Row>
        <FieldLabel htmlFor={`${idPrefix}-city`} className="mb-0.5">
          City
        </FieldLabel>
        <input
          id={`${idPrefix}-city`}
          type="text"
          autoComplete="address-level2"
          className={inputClass}
          value={safe.city || ''}
          onChange={set('city')}
          required
        />
      </Row>
      <Divider />
      <div className="grid grid-cols-2 gap-3">
        <Row>
          <FieldLabel htmlFor={`${idPrefix}-state`} className="mb-0.5">
            {stateCfg.label}
            {!stateCfg.required && (
              <>
                {' '}
                <span className="font-normal text-slate-400">(optional)</span>
              </>
            )}
          </FieldLabel>
          {stateCfg.options ? (
            <Select value={stateSelectValue} onValueChange={set('state')}>
              <SelectTrigger
                id={`${idPrefix}-state`}
                className="border-0 px-0 h-auto py-0 text-[14px] text-slate-900 shadow-none focus:ring-0 focus-visible:ring-0 hover:bg-transparent bg-transparent [&>span]:line-clamp-1"
              >
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {stateCfg.options.map((o) => (
                  <SelectItem key={o.code} value={o.code}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <input
              id={`${idPrefix}-state`}
              type="text"
              autoComplete="address-level1"
              className={inputClass}
              value={safe.state || ''}
              onChange={set('state')}
            />
          )}
        </Row>
        <Row>
          <FieldLabel htmlFor={`${idPrefix}-postal`} className="mb-0.5">
            {getPostcodeLabel(country)}
          </FieldLabel>
          <input
            id={`${idPrefix}-postal`}
            type="text"
            autoComplete="postal-code"
            className={cn(inputClass, 'uppercase')}
            value={safe.postalCode || ''}
            onChange={set('postalCode')}
            required
          />
        </Row>
      </div>
    </HCard>
  );
}

function Row({ children }) {
  return <div>{children}</div>;
}

/* Empty address with sensible country default. */
export function emptyAddress(country) {
  return {
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: country || 'GB',
  };
}

export function isAddressFilled(a) {
  return (
    !!(a && a.name && a.line1 && a.city && a.postalCode && a.country) &&
    validateState(a.state, a.country)
  );
}

/* One-line address: "line1, line2, city, STATE postcode". */
export function summariseAddress(a) {
  if (!a) return '';
  const statePostcode = [displayState(a.state, a.country), a.postalCode].filter(Boolean).join(' ');
  return [a.line1, a.line2, a.city, statePostcode].filter(Boolean).join(', ');
}
